export interface RuntimeConfig {
  readonly apiBaseUrl: string;
  readonly keycloak: {
    readonly url: string;
    readonly realm: "washbox";
    readonly clientId: "washbox-client-web";
    readonly enableLogging: boolean;
  };
}

export interface BrowserEnvironment {
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_KEYCLOAK_URL?: string;
  readonly VITE_KEYCLOAK_REALM?: string;
  readonly VITE_KEYCLOAK_CLIENT_ID?: string;
  readonly VITE_KEYCLOAK_LOGGING?: string;
}

const PRODUCTION_API_BASE_URL = "https://api.washbox-express.com/api-client/v1";
const DEVELOPMENT_API_BASE_URL = "/api-client/v1";
const KEYCLOAK_URL = "https://auth.washbox-express.com";

function parseHttpUrl(value: string, label: string): URL {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error(`${label} must be an absolute URL`);
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(`${label} must use HTTP or HTTPS`);
  }
  if (url.username || url.password || url.search || url.hash) {
    throw new Error(`${label} must not include credentials, query parameters, or fragments`);
  }

  return url;
}

function parseApiBaseUrl(value: string, development: boolean): string {
  if (value.startsWith("/")) {
    if (!development || value.replace(/\/+$/u, "") !== "/api-client/v1") {
      throw new Error(
        "A relative VITE_API_BASE_URL is allowed only for the development client API proxy",
      );
    }
    return "/api-client/v1";
  }

  const url = parseHttpUrl(value, "VITE_API_BASE_URL");
  const pathname = url.pathname.replace(/\/+$/u, "");
  if (pathname !== "/api-client/v1") {
    throw new Error("VITE_API_BASE_URL must end with /api-client/v1");
  }
  url.pathname = pathname;
  return url.toString().replace(/\/$/u, "");
}

function parseKeycloakUrl(value: string): string {
  const url = parseHttpUrl(value, "VITE_KEYCLOAK_URL");
  if (url.pathname !== "/" && url.pathname !== "") {
    throw new Error("VITE_KEYCLOAK_URL must be an origin without a path");
  }
  return url.origin;
}

function parseBoolean(value: string | undefined, label: string): boolean {
  if (value === undefined || value === "false") {
    return false;
  }
  if (value === "true") {
    return true;
  }
  throw new Error(`${label} must be true or false`);
}

export function parseRuntimeConfig(
  environment: BrowserEnvironment,
  development: boolean,
): RuntimeConfig {
  const realm = environment.VITE_KEYCLOAK_REALM ?? "washbox";
  const clientId = environment.VITE_KEYCLOAK_CLIENT_ID ?? "washbox-client-web";

  if (realm !== "washbox") {
    throw new Error("VITE_KEYCLOAK_REALM must be washbox");
  }
  if (clientId !== "washbox-client-web") {
    throw new Error("VITE_KEYCLOAK_CLIENT_ID must be washbox-client-web");
  }

  return Object.freeze({
    apiBaseUrl: parseApiBaseUrl(
      environment.VITE_API_BASE_URL ??
        (development ? DEVELOPMENT_API_BASE_URL : PRODUCTION_API_BASE_URL),
      development,
    ),
    keycloak: Object.freeze({
      url: parseKeycloakUrl(environment.VITE_KEYCLOAK_URL ?? KEYCLOAK_URL),
      realm,
      clientId,
      enableLogging: parseBoolean(environment.VITE_KEYCLOAK_LOGGING, "VITE_KEYCLOAK_LOGGING"),
    }),
  });
}

export const runtimeConfig = parseRuntimeConfig(import.meta.env, import.meta.env.DEV);
